import { useState } from 'react'
import { ReactComponent as IconPlus } from 'bootstrap-icons/icons/plus-circle.svg'
import { ReactComponent as IconGear } from 'bootstrap-icons/icons/gear.svg'

import { useCustomForms, useMyForms } from './hooks'
import CustomFormGrid from './CustomFormGrid'
import CustomFormBuilder from './CustomFormBuilder'
import MyForms from './MyForms'
import styles from './styles.module.scss'

// Panel de formularios custom (grillas configurables) dentro de la HCE V2.
function CustomForms({ consumer, resource, user }) {
  const consumerID = consumer?.id
  const forms = useCustomForms(consumerID)
  const myForms = useMyForms(!!consumerID)

  const [builder, setBuilder] = useState(null) // null | { form } | { new: true }
  const [showManage, setShowManage] = useState(false)
  const [filterId, setFilterId] = useState('') // '' = todos

  // Los formularios solo los administra quien los creó (+ isStaff para soporte).
  // teamConfig NO otorga admin acá: casi cualquier profesional tiene ese flag en
  // true por default, lo que dejaba editar/deshabilitar formularios ajenos.
  const canManage = (form) => !!user?.isStaff || (resource?.id && form.ownerResourceID === resource.id)

  const closeBuilder = (refetch) => {
    setBuilder(null)
    if (refetch) {
      forms.refetch()
      myForms.refetch()
    }
  }

  if (!consumerID) return null

  const allForms = forms.data || []
  // "Oculto" = form fuera de alcance/deshabilitado que solo aparece porque tiene
  // cargas de este paciente (canAddRecords === false). Por defecto no se listan en
  // "Todos"; quedan en el select marcados y solo se muestran si se los selecciona.
  const hiddenCount = allForms.filter((f) => f.canAddRecords === false).length
  const rows = filterId
    ? allForms.filter((f) => f.id === filterId)
    : allForms.filter((f) => f.canAddRecords !== false)

  return (
    <div className={styles.container}>
      <div className={styles.head}>
        <div className={styles.title}>Formularios</div>
        <div className={styles.spacer} />
        <select
          className={`form-select form-select-sm ${styles.formSelect} d-print-none`}
          value={filterId}
          onChange={(e) => setFilterId(e.target.value)}
          aria-label="Filtrar formularios"
        >
          <option value="">Todos los formularios{hiddenCount ? ` (${hiddenCount} oculto${hiddenCount > 1 ? 's' : ''})` : ''}</option>
          {allForms.map((f) => (
            <option key={f.id} value={f.id}>{f.name}{f.canAddRecords === false ? ' (oculto)' : ''}</option>
          ))}
        </select>
        <button type="button" className={`${styles.iconbtn} d-print-none`} onClick={() => setShowManage(true)} title="Gestionar formularios" aria-label="Gestionar formularios">
          <IconGear width="16" height="16" />
        </button>
        <button type="button" className={`${styles.btn} ${styles.primary} d-print-none`} onClick={() => setBuilder({ new: true })}>
          <IconPlus width="13" height="13" /> Nuevo formulario
        </button>
      </div>

      {forms.isLoading && <div className={styles.empty}>Cargando formularios…</div>}
      {!forms.isLoading && rows.length === 0 && (
        <div className={styles.empty}>
          {hiddenCount && !filterId
            ? 'No hay formularios activos. Hay formularios ocultos con registros: seleccionalos en el filtro de arriba.'
            : 'No hay formularios disponibles. Creá uno con “Nuevo formulario”.'}
        </div>
      )}

      {rows.map((form) => (
        <CustomFormGrid
          key={form.id}
          form={form}
          consumerID={consumerID}
          resource={resource}
          user={user}
          canManage={canManage(form)}
          onEdit={(f) => setBuilder({ form: f })}
        />
      ))}

      {builder && (
        <CustomFormBuilder
          form={builder.form || null}
          resource={resource}
          onBack={() => closeBuilder(false)}
          onSaved={() => closeBuilder(true)}
        />
      )}

      {showManage && (
        <MyForms
          resource={resource}
          user={user}
          onBack={() => { setShowManage(false); forms.refetch(); myForms.refetch() }}
          onEdit={(form) => { setShowManage(false); setBuilder({ form }) }}
        />
      )}    </div>
  )
}

export default CustomForms
